// src/lib/mapPersonToTalent.ts
export type RawPerson = any;

function titleCaseSlug(slug?: string | null) {
  if (!slug) return null;
  let s = String(slug).trim().toLowerCase();
  s = s.replace(/-market$/i, '');
  return s
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

import { toAbsoluteMediaUrl } from '@/lib/media';
import { cardProducerOf } from '@/lib/producerLabel';

export function mapPersonToTalent(person: RawPerson) {
  const id = person.id ?? person.personId ?? person.PersonId ?? person.Id ?? null;
  const stageName = person.stageName ?? person.displayName ?? person.StageName ?? person.DisplayName ?? person.name ?? null;

  // Normalize image fields from various endpoints (lanes/lean/full)
  const rawProfileKey =
    person.profileImageLocation ?? person.ProfileImageLocation ??
    person.profileImageKey ?? person.ProfileImageKey ??
    person.profileImagePath ?? person.ProfileImagePath ??
    person.profileImage ?? person.ProfileImage ?? null;
  const rawAvatar =
    person.coverImage ?? person.CoverImage ??
    person.avatarUrl ?? person.AvatarUrl ??
    person.AvatarLocation ?? person.avatarLocation ??
    person.avatarPath ?? person.AvatarPath ??
    person.thumbnailUrl ?? person.ThumbnailUrl ??
    person.thumbUrl ?? person.ThumbUrl ??
    person.profileImageUrl ?? person.ProfileImageUrl ??
    person.imageUrl ?? person.ImageUrl ?? null;
  // Always prefer the user's selected profile image
  const profileImageLocation = rawProfileKey ?? null;
  // Prefer the API-resolved avatarUrl when it's already an absolute URL (CDN-routed).
  // Only derive from profileImageLocation when avatarUrl is missing.
  let avatarUrl: string | null = null;
  if (rawAvatar && /^https?:\/\//i.test(String(rawAvatar))) {
    // Use _thumb variant — better quality than _avatar for talent card display.
    avatarUrl = String(rawAvatar).replace(/_avatar(\.[a-z0-9]+)$/i, '_thumb$1');
  } else if (profileImageLocation) {
    let key = String(profileImageLocation);
    if (/_thumb(\.|$)/i.test(key)) {
      // Already has _thumb, use as-is
    } else if (/_avatar(\.|$)/i.test(key)) {
      // Convert _avatar to _thumb
      key = key.replace(/_avatar(\.[a-z0-9]+)$/i, '_thumb$1');
    } else {
      // Add _thumb suffix
      const dot = key.lastIndexOf('.');
      key = dot > 0 ? `${key.slice(0, dot)}_thumb${key.slice(dot)}` : `${key}_thumb`;
    }
    avatarUrl = toAbsoluteMediaUrl(key);
  } else if (rawAvatar) {
    avatarUrl = toAbsoluteMediaUrl(String(rawAvatar));
  } else {
    avatarUrl = null;
  }

  const sex = ((person.sex ?? person.gender ?? person.Sex ?? '') + '').toString().toLowerCase() || null;
  const state = person.state ?? person.State ?? null;
  const metroRaw = person.metro ?? person.Metro ?? person.city ?? person.City ?? null;
  const metroSlug = metroRaw ? String(metroRaw).toLowerCase().replace(/\s+/g, '-').replace(/-market$/i, '') : null;
  // person.location is the raw integer place code — never use it as a display name
  let locationName = person.locationName ?? person.LocationName ?? null;
  if (!locationName) {
    const city = person.city ?? person.City ?? null;
    const metro = person.metro ?? person.Metro ?? null;
    const cityOrMetro = city || metro;
    const cityFmt = titleCaseSlug(cityOrMetro);
    if (cityFmt && state) locationName = `${cityFmt}, ${String(state).toUpperCase()}`;
    else if (state) locationName = String(state).toUpperCase();
  }

  // Prefer explicit age years field when provided
  const ageField = (person.ageYears ?? person.AgeYears ?? person.age ?? person.Age ?? person.dateOfBirth ?? person.DateOfBirth ?? person.dob ?? person.Dob ?? null) || null;

  // If still no locationName, consider metroName fields
  if (!locationName) {
    const metroName = person.metroName ?? person.MetroName ?? null;
    const m = metroName ? titleCaseSlug(String(metroName)) : null;
    if (m && state) locationName = `${m}, ${String(state).toUpperCase()}`;
    else if (m) locationName = m;
  }

  const blurredAvatarUrl = (() => {
    const raw = (person as any).blurredAvatarUrl ?? (person as any).BlurredAvatarUrl ?? null;
    if (!raw) return null;
    const s = String(raw);
    if (/^https?:\/\//i.test(s)) return s;
    return toAbsoluteMediaUrl(s);
  })();

  // Verified flag — read whatever shape the source endpoint emits.
  // The canonical source is /api/people/by-stagename which puts it under
  // `verification.idVerified`. Lane / top / search endpoints expose it via
  // the same Verification block. Some legacy responses use a top-level
  // `accountVerified` field. Cover all of them so any caller plugged into
  // mapPersonToTalent gets the badge automatically.
  const isVerified = Boolean(
    (person as any)?.isVerified ??
    (person as any)?.IsVerified ??
    (person as any)?.verified ??
    (person as any)?.Verified ??
    (person as any)?.verification?.idVerified ??
    (person as any)?.Verification?.IdVerified ??
    (person as any)?.accountVerified ??
    (person as any)?.AccountVerified ??
    false
  );

  // Profile likes — accept the various shapes returned by lane / search / profile endpoints.
  const likeRaw =
    (person as any)?.likeCount ??
    (person as any)?.LikeCount ??
    (person as any)?.likes ??
    (person as any)?.Likes ??
    null;
  const likeCount = Number.isFinite(Number(likeRaw)) ? Math.max(0, Number(likeRaw)) : 0;

  // The public Producer label on a card (search, search2 and the home lanes send it by the api's
  // label rule). Only a boolean true with a role value counts: some person reads carry the raw
  // People.IsProducer switch (0 or 1) with no role, and those never show the label.
  const producer = cardProducerOf(person);

  return {
    id,
    stageName,
    profileImageLocation,
    avatarUrl,
    blurredAvatarUrl,
    // Surface unsafe flag for convenience (SSR and clients can read this directly)
    unsafe: Boolean(
      (person as any)?.unsafe ??
      (person as any)?.isUnsafe ??
      (person as any)?.isUnsafeContent ??
      (person as any)?.IsUnsafeContent ??
      (person as any)?.Unsafe ?? false
    ),
    // Verified badge flag — propagates through to TalentCard.isVerified
    isVerified,
    // Profile likes — propagates through to TalentCard.likeCount
    likeCount,
    // Prefer direct age years, else number, else pass DOB for client-side derivation
    age: ageField,
    sex,
    locationName,
    metro: metroSlug,
    state: state ? String(state).toUpperCase() : null,
    tier: person.tier ?? 0,
    // Producer label fields, propagated to TalentCard (false and nulls for a talent).
    isProducer: producer != null,
    producerRole: producer?.role ?? null,
    producerBusinessName: producer?.businessName ?? null,
    raw: person,
  };
}
